import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBrokers, getArticles } from '../lib/api';
import { BrokerCard } from '../components/broker/BrokerCard';
import { ComparisonTable } from '../components/broker/ComparisonTable';
import { HomeHero } from '../components/layout/HomeHero';
import { SEO, buildOrganizationSchema, buildWebSiteSchema, buildComparisonSchema } from '../components/SEO';
import { ArrowRight, BookOpen, Shield, TrendingUp, BarChart3 } from 'lucide-react';

export default function HomePage() {
  const [brokers, setBrokers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brokersRes, articlesRes] = await Promise.all([getBrokers(), getArticles()]);
        setBrokers(brokersRes.data);
        setArticles(articlesRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  const homeJsonLd = [buildOrganizationSchema(), buildWebSiteSchema(), buildComparisonSchema(brokers)];

  return (
    <div data-testid="home-page">
      <SEO
        canonical="/"
        jsonLd={homeJsonLd}
      />
      <HomeHero />

      {/* Trust Indicators */}
      <section className="bg-white border-b border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: Shield, label: 'Regulation Verified', desc: 'All brokers checked' },
              { icon: BarChart3, label: 'Independent Scoring', desc: 'Objective methodology' },
              { icon: TrendingUp, label: '5 Brokers Compared', desc: 'Side-by-side analysis' },
              { icon: BookOpen, label: 'Educational Guides', desc: 'Free learning resources' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-2">
                <item.icon className="w-6 h-6 text-blue-600" />
                <span className="text-sm font-semibold text-slate-900">{item.label}</span>
                <span className="text-xs text-slate-500">{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Brokers Grid */}
      <section className="py-16 sm:py-20" data-testid="featured-brokers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-3">
              Featured Broker Reviews
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Explore our independently reviewed brokers. All brokers are presented with equal consideration based on our objective assessment criteria.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {brokers.filter(b => b.is_featured).map((broker) => (
              <BrokerCard key={broker.slug} broker={broker} />
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-white border-y border-slate-200" data-testid="comparison-table-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-3">
              Broker Comparison
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              A neutral comparison of key features across all reviewed brokers.
            </p>
          </div>
          <ComparisonTable brokers={brokers} />
          <div className="text-center mt-8">
            <Link
              to="/compare"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              data-testid="compare-all-btn"
            >
              Compare All Brokers in Detail <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Educational Section */}
      <section className="py-16 sm:py-20" data-testid="educational-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 font-['Outfit'] mb-3">
              Educational Guides
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Build your trading knowledge with our comprehensive guides on broker selection, risk management, and financial planning.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {articles.slice(0, 3).map((article) => (
              <Link
                key={article.slug}
                to={`/guides/${article.slug}`}
                className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-slate-300 hover:shadow-sm transition-all duration-200"
                data-testid={`article-card-${article.slug}`}
              >
                <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">{article.category}</span>
                  <h3 className="text-lg font-semibold text-slate-900 mt-1.5 mb-2 group-hover:text-blue-600 transition-colors font-['Outfit']">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400 mt-3">
                    {article.reading_time}
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 h-10 px-6 transition-colors"
              data-testid="view-all-guides-btn"
            >
              View All Guides <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
